import React, { useState, useRef, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Tabs,
  Tab,
  Typography,
  Button,
  Box,
  CircularProgress,
  Switch,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import EditOptionDialog from "../Dialogs/EditOptionDialog";
import DeleteConfirmationDialog from "../Dialogs/DeleteConfirmationDialog";
import AddIcon from "@mui/icons-material/Add";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { DropDownCategoryEnumModel, ILookupItemModel } from "../../types/types";
import { AppDispatch } from "../../store/store";
import {
  addCategoryVariable,
  deleteCategoryVariable,
  showHideCategoryVariable,
  updateCategoryVariable,
} from "../../store/modules/common/commonActions";
import LoadingDialog from "../Dialogs/LoadingDialog";
import { OperationStatus } from "../../types/models";

interface DropdownConfig {
  id: DropDownCategoryEnumModel;
  label: string;
  allowPaging: boolean;
  hasSystemOptions?: boolean;
  key: string;
}

const DropdownOptionsManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const listContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const {
    userData,
    addCategoryVariableState,
    updateCategoryVariableState,
    deleteCategoryVariableState,
    hideCategoryVariableState,
  } = useSelector((state: RootState) => state.commonData);
  const [editingOptionId, setEditingOptionId] = useState<number | undefined>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDropdownId, setEditingDropdownId] = useState<
    DropDownCategoryEnumModel | undefined
  >();
  const [editingOptionName, setEditingOptionName] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    dropdownId: DropDownCategoryEnumModel;
    optionId: number;
    optionName: string;
  } | null>(null);

  // Snackbar state for error messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error" as "error" | "warning" | "info" | "success",
  });

  // Add Option Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingDropdownId, setAddingDropdownId] = useState<
    DropDownCategoryEnumModel | undefined
  >();
  const [newOptionValue, setNewOptionValue] = useState("");

  const dropdowns: DropdownConfig[] = [
    {
      id: DropDownCategoryEnumModel.TransactionType,
      label: "Transaction Types",
      allowPaging: false,
      hasSystemOptions: true,
      key: "transactionType",
    },
    {
      id: DropDownCategoryEnumModel.Source,
      label: "Sources",
      allowPaging: false,
      hasSystemOptions: true,
      key: "source",
    },
    {
      id: DropDownCategoryEnumModel.Reason,
      label: "Reasons",
      allowPaging: false,
      hasSystemOptions: true,
      key: "reason",
    },
    {
      id: DropDownCategoryEnumModel.Area,
      label: "Custom Areas",
      allowPaging: false,
      hasSystemOptions: false,
      key: "area",
    },
    {
      id: DropDownCategoryEnumModel.SpecialGroup,
      label: "Special Groups",
      allowPaging: false,
      hasSystemOptions: false,
      key: "specialGroups",
    },
    {
      id: DropDownCategoryEnumModel.SpecialTag,
      label: "Special Tags",
      allowPaging: false,
      hasSystemOptions: false,
      key: "specialTags",
    },
    {
      id: DropDownCategoryEnumModel.ReferralOutType,
      label: "Referral Out Types",
      allowPaging: false,
      hasSystemOptions: true,
      key: "referralOutTypes",
    },
  ];

  const handleAddOption = (dropdown: DropdownConfig, value: string) => {
    if (!value?.trim()) return;
    dispatch(addCategoryVariable(dropdown.id, value.trim()));
  };

  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields
  );

  const handleEditOption = (
    dropdownId: DropDownCategoryEnumModel,
    option: ILookupItemModel
  ) => {
    setEditingDropdownId(dropdownId);
    setEditingOptionId(option.id!);
    setEditingOptionName(option.name || "");
    setEditDialogOpen(true);
  };

  const handleSaveEditDialog = (newName: string) => {
    dispatch(
      updateCategoryVariable(editingDropdownId!, editingOptionId!, newName)
    );
    setEditDialogOpen(false);
    setEditingOptionId(undefined);
    setEditingDropdownId(undefined);
    setEditingOptionName("");
  };

  const handleDeleteOption = (
    dropdownId: DropDownCategoryEnumModel,
    optionId: number,
    optionName: string
  ) => {
    setPendingDelete({ dropdownId, optionId, optionName });
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = () => {
    dispatch(
      deleteCategoryVariable(pendingDelete!.dropdownId, pendingDelete!.optionId)
    );
    setDeleteConfirmationOpen(false);
    setPendingDelete(null);
  };

  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTo({
        top: listContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [addCategoryVariableState.fulfilled]);

  useEffect(() => {
    if (deleteCategoryVariableState.status === OperationStatus.Failed) {
      setSnackbar({
        open: true,
        message: `${
          deleteCategoryVariableState.message || "Failed to delete option."
        }`,
        severity: "error",
      });
    }
  }, [deleteCategoryVariableState]);

  useEffect(() => {
    if (addCategoryVariableState.error) {
      setSnackbar({
        open: true,
        message: `${addCategoryVariableState.error || "Failed to add option."}`,
        severity: "error",
      });
    }
  }, [addCategoryVariableState]);

  // Handle closing the Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const visibileDropdowns = dropdowns.filter(
    (dropdown) => !hiddenFields.some((field) => field.name === dropdown.key)
  );
  function getCategoryOptions(dropdown: DropdownConfig): ILookupItemModel[] {
    switch (dropdown.id) {
      case DropDownCategoryEnumModel.Reason:
        return userData.data?.reasons ?? [];
      case DropDownCategoryEnumModel.Source:
        return userData.data?.sources ?? [];
      case DropDownCategoryEnumModel.Area:
        return userData.data?.areas ?? [];
      case DropDownCategoryEnumModel.SpecialGroup:
        return userData.data?.specialGroups ?? [];
      case DropDownCategoryEnumModel.SpecialTag:
        return userData.data?.specialTags ?? [];
      case DropDownCategoryEnumModel.TransactionType:
        return userData.data?.transactionTypes ?? [];
      case DropDownCategoryEnumModel.ReferralOutType:
        return userData.data?.referralOutTypes ?? [];
    }

    return [];
  }

  const getPendingDeleteOptionName = () => {
    if (!pendingDelete) return "";

    return pendingDelete.optionName || "";
  };

  return (
    <>
      <LoadingDialog
        open={
          updateCategoryVariableState.status === OperationStatus.Pending ||
          deleteCategoryVariableState.status === OperationStatus.Pending ||
          addCategoryVariableState.pending
        }
      />
      <Box sx={{ width: "100%", p: 0 }}>
        {/* Header Navigation - Tabs for medium+ screens, Dropdown for small screens */}
        <Box
          sx={{
            background: "#F7F9FB",
            borderRadius: "8px",
            py: 1,
            mb: 3,
            display: "flex",
            alignItems: "center",
            minHeight: 56,
          }}
        >
          {/* Tabs for medium and up screens */}
          <Box sx={{ display: { xs: "none", md: "block" }, width: "100%" }}>
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              sx={{
                minHeight: 40,
                "& .MuiTabs-flexContainer": { gap: 0 },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  minHeight: 40,
                  borderRadius: "8px 8px 0 0",
                  color: "#222",
                  background: "transparent",
                  "&.Mui-selected": {
                    background: "#fff",
                    color: "#1976d2",
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {visibileDropdowns.map((dropdown, index) => (
                <Tab label={dropdown.label} key={index} />
              ))}
            </Tabs>
          </Box>

          {/* Dropdown for small screens */}
          <Box
            sx={{ display: { xs: "block", md: "none" }, width: "100%", px: 2 }}
          >
            <FormControl fullWidth>
              <InputLabel id="dropdown-options-label">
                Select Category
              </InputLabel>
              <Select
                labelId="dropdown-options-label"
                value={selectedTab}
                onChange={(e) => setSelectedTab(Number(e.target.value))}
                label="Select Category"
                sx={{
                  fontWeight: 600,
                  "& .MuiSelect-select": {
                    py: 1.5,
                  },
                }}
              >
                {visibileDropdowns.map((dropdown, index) => (
                  <MenuItem value={index} key={index}>
                    {dropdown.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Main Content */}
        {visibileDropdowns.map((dropdown, index) => (
          <div hidden={selectedTab !== index} key={dropdown.id}>
            <Box
              sx={{
                background: "#fff",
                borderRadius: "12px",
                p: { xs: 2, md: 4 },
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
                mb: 1,
                minHeight: 400,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  // gap: 2,

                  justifyContent: "flex-end",
                  // background: "#F7F9FB",
                  borderRadius: "8px",
                  // px: 2,
                  // py: 1.5,
                }}
              >
                <Button
                  disabled={addCategoryVariableState.pending}
                  variant="contained"
                  size="small"
                  startIcon={
                    addCategoryVariableState.pending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AddIcon />
                    )
                  }
                  onClick={() => {
                    setAddingDropdownId(dropdown.id);
                    setNewOptionValue("");
                    setAddDialogOpen(true);
                  }}
                  sx={{
                    fontWeight: 600,

                    fontSize: "1rem",
                    background: "primary.main",
                    borderRadius: "6px",
                    px: 2,
                    py: 1,
                    boxShadow: "none",
                    "&:hover": { background: "#1565c0" },
                  }}
                >
                  Option
                </Button>
              </Box>

              {!dropdown.allowPaging && (
                <>
                  {dropdown.hasSystemOptions && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "#222",
                          mb: 1.5,
                          fontSize: "1.1rem",
                        }}
                      >
                        System Options
                      </Typography>
                      <Box>
                        {(getCategoryOptions(dropdown) || [])
                          .filter((option) => option.system)
                          .map((option) => (
                            <Box
                              key={option.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                background: "#F7F9FB",
                                borderRadius: "8px",
                                px: 2,
                                py: 1.5,
                                mb: 1.5,
                              }}
                            >
                              {hideCategoryVariableState?.status ===
                                OperationStatus.Pending &&
                              option.id ===
                                hideCategoryVariableState.variableId &&
                              dropdown.id ===
                                hideCategoryVariableState.category ? (
                                <CircularProgress
                                  size={24}
                                  sx={{ mr: 2, color: "#1976d2" }}
                                />
                              ) : (
                                <Switch
                                  //here
                                  checked={option.deleted !== true}
                                  onChange={() => {
                                    dispatch(
                                      showHideCategoryVariable(
                                        dropdown.id,
                                        option.id!,
                                        option.deleted !== true
                                      )
                                    );
                                  }}
                                  sx={{
                                    mr: 2,
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: "primary.main",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: "#1976d2",
                                      },
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    option.deleted === true
                                      ? "#B0B0B0"
                                      : "#222",
                                  fontSize: "1.1rem",
                                  mr: 1,
                                }}
                              >
                                {option.name}
                              </Typography>
                              {/* <Box
                                sx={{
                                  background: "#E3EDFB",
                                  color: "#1976d2",
                                  fontWeight: 600,
                                  fontSize: "0.85rem",
                                  borderRadius: "6px",
                                  px: 1.5,
                                  py: 0.5,
                                  ml: 1,
                                }}
                              >
                                System
                              </Box> */}
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  )}

                  {/* Custom Options */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        mb: 1.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      Custom Options
                    </Typography>
                    <Box>
                      {(getCategoryOptions(dropdown) || [])
                        .filter((option) => !option.system)
                        .map((option) => (
                          <Box
                            key={option.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              background: "#F7F9FB",
                              borderRadius: "8px",
                              px: 2,
                              py: 1.5,
                              mb: 1.5,
                            }}
                          >
                            {/* <Switch
                              checked={option.deleted !== true}
                              sx={{
                                mr: 2,
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "primary.main",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor: "#1976d2",
                                  },
                              }}
                            /> */}
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color:
                                  option.deleted === true ? "#B0B0B0" : "#222",
                                fontSize: "1.1rem",
                                mr: 1,
                              }}
                            >
                              {option.name}
                            </Typography>
                            {/* <Box
                              sx={{
                                background: "#E3F7ED",
                                color: "#2E7D32",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                borderRadius: "6px",
                                px: 1.5,
                                py: 0.5,
                                ml: 1,
                              }}
                            >
                              Custom
                            </Box> */}
                            <Box sx={{ flexGrow: 1 }} />

                            <IconButton
                              size="small"
                              onClick={() =>
                                handleEditOption(dropdown.id, option)
                              }
                              sx={{
                                minWidth: 0,
                                p: 1,
                                color: "#222",
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                handleDeleteOption(
                                  dropdown.id,
                                  option.id!,
                                  option.name!
                                )
                              }
                              sx={{
                                minWidth: 0,
                                p: 1,
                                color: "#D32F2F",
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                </>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  justifyContent: "flex-end",
                  // background: "#F7F9FB",
                  borderRadius: "8px",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Button
                  disabled={addCategoryVariableState.pending}
                  variant="contained"
                  size="small"
                  startIcon={
                    addCategoryVariableState.pending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AddIcon />
                    )
                  }
                  onClick={() => {
                    setAddingDropdownId(dropdown.id);
                    setNewOptionValue("");
                    setAddDialogOpen(true);
                  }}
                  sx={{
                    fontWeight: 600,

                    fontSize: "1rem",
                    background: "primary.main",
                    borderRadius: "6px",
                    px: 2,
                    py: 1,
                    boxShadow: "none",
                    "&:hover": { background: "#1565c0" },
                  }}
                >
                  Option
                </Button>
              </Box>
            </Box>
          </div>
        ))}
      </Box>
      <EditOptionDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveEditDialog}
        optionName={editingOptionName}
      />
      <EditOptionDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={(name) => {
          if (addingDropdownId !== undefined) {
            handleAddOption(
              visibileDropdowns.find((d) => d.id === addingDropdownId)!,
              name
            );
          }
          setAddDialogOpen(false);
          setAddingDropdownId(undefined);
          setNewOptionValue("");
        }}
        optionName={newOptionValue}
        dialogTitle="Add New Option"
      />
      <DeleteConfirmationDialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onConfirm={confirmDelete}
        optionName={getPendingDeleteOptionName()}
      />

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DropdownOptionsManager;
